import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modal";
import { MoneyText } from "@/components/MoneyText";
import { NumPad } from "@/components/NumPad";
import type { CartLine } from "@/lib/types";

import { localized } from "./labels";

/** ✎ 価格を変える — a new 税込 price + reason (the manager approves when the cashier may not). */
export function PriceOverrideModal({
  line,
  onClose,
  onApply,
  onReset,
}: {
  line: CartLine | null;
  onClose: () => void;
  onApply: (line: CartLine, price: number, reason: string) => Promise<boolean>;
  onReset: (line: CartLine) => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!line) {
    return null;
  }
  const price = Number.parseInt(value || "", 10);
  const valid = Number.isInteger(price) && price >= 0 && reason.trim().length > 0;
  const close = () => {
    setValue("");
    setReason("");
    onClose();
  };
  return (
    <Modal
      open
      title={`${t("override.title")} — ${localized(line.title, i18n.resolvedLanguage ?? "ja")}`}
      onClose={close}
      busy={busy}
      size="lg"
      footer={
        <>
          {line.overridePrice !== null ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void onReset(line).finally(() => {
                  setBusy(false);
                  close();
                });
              }}
              className="mr-auto min-h-pos-touch rounded-md border px-6 text-lg"
            >
              {t("override.reset")}
            </button>
          ) : null}
          <button
            type="button"
            onClick={close}
            disabled={busy}
            className="min-h-pos-touch rounded-md border px-6 text-lg"
          >
            {t("app.cancel")}
          </button>
          <button
            type="button"
            disabled={!valid || busy}
            onClick={() => {
              setBusy(true);
              void onApply(line, price, reason.trim()).then((ok) => {
                setBusy(false);
                if (ok) {
                  close();
                }
              });
            }}
            className="min-h-pos-touch rounded-md bg-primary px-8 text-lg font-semibold text-primary-foreground disabled:opacity-50"
          >
            {t("override.apply")}
          </button>
        </>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <p>
            {t("override.current")}: <MoneyText amount={line.effectiveUnitPrice} />
          </p>
          <NumPad
            label={t("override.new_price")}
            value={value}
            onChange={setValue}
            money
            maxLength={9}
          />
        </div>
        <label className="flex flex-col gap-1">
          <span className="font-medium">{t("override.reason")}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={200}
            rows={4}
            className="rounded-md border p-3 text-lg"
          />
        </label>
      </div>
    </Modal>
  );
}
